import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import AppraisalCertificate from './AppraisalCertificate';
import debounce from 'lodash.debounce';
import { pdf } from '@react-pdf/renderer';
import './styles.css';
import { API_BASE_URL } from "../config/config";


const SearchCertificate = () => {
    const navigate = useNavigate();

    const handleClick = (contact) => {
        window.scrollTo(0, 0); // Scroll to the top of the page
        navigate('/appraisal-request', { state: { contactId: contact.ContactId } });
    };



    const [forms, setForms] = useState([]); // Initialize with an empty array

    const [modalVisible, setModalVisible] = useState(false);
    const [plusModalVisible, setPlusModalVisible] = useState(false); // New state for the plus modal
    const [contacts, setContacts] = useState([]); // State for fetched CRM contacts
    const [sortOrder, setSortOrder] = useState('Newest First');
    const [loadingContacts, setLoadingContacts] = useState(false); // Loading state for contacts
    const [search, setSearch] = useState(''); // State for search input
    const [selectedForm, setSelectedForm] = useState(null);
    const [previewImageSrc, setPreviewImageSrc] = useState('/previewButtonHeader.svg');
    const [saveButtonImageSrc, setSaveButtonImageSrc] = useState('/saveButtonHeader.svg');
    const [savedFormsImageSrc, setSavedFormsImageSrc] = useState('/savedFormsButtomHeader.svg');
    const [isLoading, setIsLoading] = useState(false);
    const [onClickHandler, setOnClickHandler] = useState(() => () => { });
    const [searchInitiated, setSearchInitiated] = useState(false); // Track if a search has been initiated
    const [searchTerm, setSearchTerm] = useState('');



    const fetchContactsDebounced = useRef(
        debounce(async (value) => {
            setSearch(value);
            await fetchContacts();
        }, 300)
    ).current;


    const fetchCertificates = async (searchTerm = '') => {
        try {
            const url = `${API_BASE_URL}/api/certificates`;
    
            console.log('Fetching certificates from URL:', url);
    
            const response = await fetch(url);
            if (!response.ok) {
                console.error('Error response from API:', await response.text());
                throw new Error('Failed to fetch certificates');
            }
    
            const data = await response.json();
            console.log('Fetched certificates:', data);
    
            // Debug log to show each item's fields and the search term
            console.log(`Search term: "${searchTerm}"`);
            data.forEach((item) => {
                console.log('Checking item:', {
                    vin: item['VIN Number'],
                    email: item.Email,
                    dateCreated: item.DateCreated,
                    name: item.Name,
                });
            });
    
            // Filter data based on searchTerm
            const filteredData = data.filter((item) => {
                const matchesVIN =
                    item['VIN Number'] &&
                    item['VIN Number'].toLowerCase().includes(searchTerm.toLowerCase());
                const matchesEmail =
                    Array.isArray(item.Email) &&
                    item.Email.some(
                        (emailEntry) =>
                            emailEntry.Text &&
                            emailEntry.Text.toLowerCase().includes(searchTerm.toLowerCase())
                    );
    
                // Handle DateCreated in both ISO and American formats
                const matchesDateCreated = item.DateCreated && (() => {
                    const date = new Date(item.DateCreated);
                    const isoFormat = date.toISOString().split('T')[0]; // YYYY-MM-DD
                    const americanFormat = date.toLocaleDateString('en-US'); // MM/DD/YYYY
                    return (
                        isoFormat.includes(searchTerm) ||
                        americanFormat.includes(searchTerm)
                    );
                })();
    
                const matchesName =
                    typeof item.Name === 'string' &&
                    item.Name.toLowerCase().includes(searchTerm.toLowerCase());
    
                // Debug: Log if the item matches any condition
                if (matchesVIN || matchesEmail || matchesDateCreated || matchesName) {
                    console.log('Matched item:', {
                        vin: item['VIN Number'],
                        email: item.Email,
                        dateCreated: item.DateCreated,
                        name: item.Name,
                    });
                }
    
                return matchesVIN || matchesEmail || matchesDateCreated || matchesName;
            });
    
            // Debug: Show filtered results
            console.log('Filtered certificates:', filteredData);
    
            // Sort and update state
            const sortedCertificates = filteredData.sort(
                (a, b) => new Date(b.DateCreated) - new Date(a.DateCreated)
            );
            setForms(sortedCertificates);
        } catch (error) {
            console.error('Error fetching certificates:', error);
        }
    };
    
    
    
    

    useEffect(() => {
        // Debounce the API call for search
        const delayDebounceFn = setTimeout(() => {
            fetchCertificates(searchTerm); // Fetch filtered forms
        }, 300); // 300ms debounce time
    
        return () => clearTimeout(delayDebounceFn); // Cleanup debounce
    }, [searchTerm]); // Run effect when `searchTerm` changes



    const fetchContacts = async () => {
        if (!search.trim()) {
            console.log('No search term entered.');
            return;
        }

        try {
            setLoadingContacts(true);
            let allResults = [];
            let page = 1;
            let hasMore = true;

            while (hasMore) {
                const response = await fetch(
                    `${API_BASE_URL}/api/crm/get-contacts?search=${encodeURIComponent(search)}&page=${page}&limit=200`
                );
                if (!response.ok) throw new Error('Failed to fetch contacts');

                const data = await response.json();
                console.log('Fetched Data:', data);

                allResults = [...allResults, ...data.results];
                hasMore = data.hasMoreResults;
                page++;
            }

            setContacts(allResults); // Update state with fetched contacts
        } catch (error) {
            console.error('Error fetching contacts:', error);
            setContacts([]);
        } finally {
            setLoadingContacts(false);
        }
    };


    const handleSearch = async () => {
        if (!search.trim()) {
            console.error('Search term cannot be empty');
            setContacts([]);
            return;
        }

        setSearchInitiated(true); // Mark search as initiated
        setLoadingContacts(true); // Start loading state

        try {
            const url = `${API_BASE_URL}/api/crm/get-contacts?search=${encodeURIComponent(search)}`;
            console.log('Fetching contacts from:', url); // Log the API URL for debugging

            const response = await fetch(url);

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Failed to fetch contacts: ${errorText}`);
            }

            const data = await response.json();
            console.log('Search results:', data); // Log the response data

            setContacts(data.results || []); // Update contacts state
        } catch (error) {
            console.error('Error fetching contacts:', error.message);
            setContacts([]); // Clear contacts on error
        } finally {
            setLoadingContacts(false); // Stop loading state
        }
    };

    const handleDeleteForm = async () => {
        if (!selectedForm?._id) {
            console.error('No form selected for deletion.');
            return;
        }

        try {
            const response = await fetch(`${API_BASE_URL}/api/deleteForm/${selectedForm._id}`, {
                method: 'DELETE',
            });

            if (response.ok) {
                alert('Form deleted successfully!');
                // Remove the deleted form from the frontend state
                setForms((prevForms) => prevForms.filter((form) => form._id !== selectedForm._id));
            } else {
                const errorData = await response.json();
                console.error('Failed to delete form:', errorData.error);
                alert('Failed to delete the form.');
            }
        } catch (error) {
            console.error('Error deleting form:', error);
            alert('An error occurred while deleting the form.');
        } finally {
            setModalVisible(false); // Close the modal after deletion attempt
        }
    };


    const filteredContacts = contacts; // No need to filter on the frontend

    console.log('Search term:', search);
    console.log('Filtered contacts:', filteredContacts);


    // Later, you can assign a function to `onClickHandler`
    const assignHandler = () => {
        setOnClickHandler(() => {
            console.log("Button clicked!");
        });
    };

    const handleSort = (order) => {
        const sortedForms = [...forms]; // Create a shallow copy to avoid mutating the original array

        switch (order) {
            case 'Newest First':
                sortedForms.sort((a, b) => new Date(b.DateCreated) - new Date(a.DateCreated));
                break;
            case 'Oldest First':
                sortedForms.sort((a, b) => new Date(a.DateCreated) - new Date(b.DateCreated));
                break;
            case 'A-Z':
                sortedForms.sort((a, b) => {
                    const nameA = (a['VIN Number'] || '').toLowerCase();
                    const nameB = (b['VIN Number'] || '').toLowerCase();
                    return nameA.localeCompare(nameB);
                });
                break;
            default:
                break;
        }

        setForms(sortedForms); // Update the forms state with the sorted array
    };



    const handleGenerateAppraisalCertificate = async () => {
        setIsLoading(true);
        try {
            const formData = {
                vin: '123456789ABCDEFG', // Replace with actual form data
                dateOfLoss: '2024-11-25', // Replace with actual form data
                selectedCompany: 'ae',   // Replace with actual form data
            };

            const pdfBlob = await pdf(<AppraisalCertificate formData={formData} />).toBlob();

            // Trigger the download
            const url = URL.createObjectURL(pdfBlob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'AppraisalCertificate.pdf';
            link.click();

            // Revoke the URL to release memory
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error generating the PDF:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (plusModalVisible) {
            fetchContacts();
        }
    }, [plusModalVisible]);

    useEffect(() => {
        const fetchCertificates = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/api/getCertificates`);
                if (!response.ok) {
                    console.error('Failed to fetch certificates:', response.statusText);
                    throw new Error('Failed to fetch certificates');
                }

                const data = await response.json();
                console.log('Certificates fetched from backend:', data); // Debug log

                if (!Array.isArray(data)) {
                    console.error('Invalid response format:', data); // Log invalid responses
                    throw new Error('Invalid response format');
                }

                setForms(data); // Update the forms state
            } catch (error) {
                console.error('Error fetching certificates:', error.message);
            }
        };

        fetchCertificates();
    }, []);


    useEffect(() => {
        const fetchCertificates = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/api/getCertificates`);
                if (!response.ok) {
                    throw new Error('Failed to fetch certificates');
                }

                const data = await response.json();
                console.log('Certificates fetched from backend:', data); // Log the fetched data
                setForms(data); // Update the forms state
            } catch (error) {
                console.error('Error fetching certificates:', error);
            }
        };




        fetchCertificates();
    }, []);


    return (
        <div
            style={{
                background: 'linear-gradient(237deg, rgba(39, 39, 39, 0) 70%, rgba(0, 195, 116, 0.4) 120%), #272727',
                backgroundAttachment: 'fixed', // This fixes the background in place
                backgroundSize: 'cover', // Ensure the background covers the whole page
                minHeight: '100vh', // Ensure the container takes at least the full viewport height
                color: '#fff', // Ensure text color contrasts with background
                display: 'flex',
                flexDirection: 'column', // Stack elements vertically
                justifyContent: 'center', // Center content vertically
                alignItems: 'center', // Center content horizontally
                textAlign: 'center', // Align text in the center

            }}
        >
            <header>
                <Link to="/">
                    <div className="logo">
                        <img src="/ae.svg" alt="Logo" className='header-image' />
                    </div>
                </Link>

            </header>
            <div className="containerForms">
                <div className='titleDiv'>
                </div>
                <section className="form-section">
                    <div className="headline-container">
                        <h2 className="forms-header">Appraisal Certificate</h2>
                        <div className="titleBottomBorder"> </div>
                        <img src='/addButton.svg' alt='addCertificate' className='buttonPlus' onClick={() => {
                            console.log('Plus button clicked');
                            setPlusModalVisible(true); // Show modal
                        }} />
                        <input
                        type="text"
                        placeholder="Search for vin, email or date"
                        className="search-bar"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>


                    <div className="orderByContainer">
                        <p className="orderByLabel">Sort by: </p>
                        <select
                            className="orderByDropdown"
                            value={sortOrder}
                            onChange={(e) => {
                                const selectedOrder = e.target.value;
                                setSortOrder(selectedOrder);
                                handleSort(selectedOrder);
                            }}
                        >
                            <option value="Newest First">Newest First</option>
                            <option value="Oldest First">Oldest First</option>
                            <option value="A-Z">A-Z</option>
                        </select>
                    </div>

                    <div className='vindatenameContainer'>
                        <p className='vindatenamewrites1'>VIN</p>
                        <p className='vindatenamewrites2'>Creation Date</p>
                        <p className='vindatenamewrites3'>Email</p>
                    </div>

                    <div className="scroll-container">
                        {forms.length === 0 ? (
                            <p>No forms available.</p>
                        ) : (
                            forms.map((form) => (
                                <div className="form-group" key={form._id}>
                                    <div className="custom-row">
                                        <div className="rowInfo">
                                            <div className="infoForm">
                                                <span className="custom-data-form">{form?.['VIN Number'] || 'N/A'}</span>
                                            </div>
                                            <div className="infoForm" style={{paddingLeft: '30px'}}>
                                                <span className="custom-data-form">
                                                    {new Date(form?.['DateCreated']).toLocaleDateString('en-US') || 'N/A'}
                                                </span>
                                            </div>
                                            <div className="infoForm" style={{paddingLeft: '85px'}}>
                                                <span className="custom-data-form">{form?.Email?.[0]?.Text || 'No Email Available'}</span>
                                            </div>
                                        </div>

                                        <div className="custom-button-group">
                                            <button
                                                className="custom-delete-button"
                                                onClick={() => {
                                                    setModalVisible(true);
                                                    setSelectedForm(form);
                                                }}
                                            >
                                                <img src="/deleteButton.svg" alt="Delete" width="53" height="53" />
                                            </button>

                                            <button
                                                className="custom-preview-button"
                                                onClick={() => {
                                                    if (form?.pdfUrl) {
                                                        window.open(form.pdfUrl, '_blank');
                                                    } else {
                                                        alert('No PDF available for this form.');
                                                    }
                                                }}
                                            >
                                                <img src="/previewButtonHeader.svg" alt="Preview" width="45" height="45" />
                                            </button>

                                            <Link to={`/edit-appraisal-request/${form._id}`}>
                                                <button className="custom-edit-button">
                                                    <img src="/editButton.svg" alt="Edit" width="45" height="45" />
                                                </button>
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                </section>
            </div>
            {modalVisible && (
                <div className="confirmation-modal">
                    <img className="modalImage" src="/deleteButton.svg" alt="delete" />
                    <h className="modalH">Are you sure?</h>
                    <p className="modalP">Do you really want to delete this form? This process cannot be undone.</p>
                    <div className="modalButtonscontainer">
                        <button className="buttonModalCancel" onClick={() => setModalVisible(false)}>
                            Cancel
                        </button>
                        <button className="buttonModalClear" onClick={handleDeleteForm}>
                            Delete
                        </button>
                    </div>
                </div>
            )}
            {plusModalVisible && (
                <div className="plus-modal">
                    <div className="plus-modal-content">
                        <form
                            onSubmit={(e) => {
                                e.preventDefault();
                                handleSearch(); // Trigger the search on form submit
                            }}
                        >
                            <div className="search-bar-container-modal">
                                <input
                                    type="text"
                                    id="search"
                                    placeholder="Enter search term..."
                                    className="search-bar-modal"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)} // Update search term
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleSearch(); // Trigger search on Enter key press
                                        }
                                    }}

                                />
                                <button
                                    className="search-button-modal"
                                    type="button"
                                    onClick={handleSearch}
                                >
                                    <img
                                        src="/searchIcon.png"
                                        alt="Search Icon"
                                        className='searchIconModal'
                                    />
                                </button>
                            </div>
                        </form>

                        <div className="search-results-modal">
                            {!searchInitiated && <p>Enter a search term to get started.</p>}
                            {searchInitiated && loadingContacts && <p>Loading contacts...</p>}
                            {searchInitiated && !loadingContacts && contacts.length === 0 && <p>No contacts found.</p>}
                            {searchInitiated && !loadingContacts && contacts.length > 0 && (
                                contacts.map((contact, index) => {
                                    const name = `${contact?.Name?.FirstName || ''} ${contact?.Name?.MiddleName || ''} ${contact?.Name?.LastName || ''}`.trim();
                                    const email = contact?.Email?.[0]?.Text || 'No email available';
                                    const phone = contact?.Phone?.[0]?.Text || 'No phone available';

                                    return (
                                        <div
                                            className="result-item-modal"
                                            key={index}
                                            onClick={() => handleClick(contact)} // Redirect with data
                                            style={{ cursor: 'pointer' }} // Make the div look clickable
                                        >
                                            <p className="resultValue">{name}</p>
                                            <p className="resultValue">{email}</p>
                                            <p className="resultValue">{phone}</p>
                                        </div>
                                    );
                                })
                            )}
                        </div>

                        <button
                            onClick={() => setPlusModalVisible(false)} // Close modal
                            className="close-button-modal"
                        >
                            <img src="/deleteButton.svg" alt="Close" width="30" height="30" />
                        </button>
                    </div>
                </div>
            )}



        </div>

    )

}

export default SearchCertificate;